import { useMemo, useRef, useState } from 'react';
import { ActionIcon, Box, Combobox, Loader, Text, TextInput, Tooltip, useCombobox } from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { useDebouncedValue, useHotkeys } from '@mantine/hooks';
import { useNavigate } from '@tanstack/react-router';
import { globalSearchQueryOptions } from '@api/search/searchQueries';
import { useQuery } from '@tanstack/react-query';
import classes from './SearchBarWithSuggestions.module.css';

interface SearchSuggestion {
  id: string;
  type: 'customer' | 'system' | 'license' | 'hardware';
  label: string;
  description: string;
  customerId?: string;
  systemId?: string;
  licenseId?: string;
  serialNumber?: string;
}

interface SearchBarWithSuggestionsProps {
  centered?: boolean;
  onSearch?: (query: string) => void;
  initialValue?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function SearchBarWithSuggestions({
                                           centered = false,
                                           onSearch,
                                           initialValue = '',
                                           size,
                                         }: SearchBarWithSuggestionsProps) {
  const [query, setQuery] = useState(initialValue);
  const [debouncedQuery] = useDebouncedValue(query, 500);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useHotkeys([['mod+/', () => inputRef.current?.focus()]]);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const shouldSearch = debouncedQuery.trim().length >= 2;
  const searchQuery = useQuery(globalSearchQueryOptions(debouncedQuery, shouldSearch));
  const isLoading = searchQuery.isFetching;

  // Transform API responses into grouped suggestions (limit to 10 per category for typeahead)
  const groupedSuggestions = useMemo(() => {
    const customers: SearchSuggestion[] = [];
    const systems: SearchSuggestion[] = [];
    const licenses: SearchSuggestion[] = [];
    const hardware: SearchSuggestion[] = [];

    searchQuery.data?.customers.slice(0, 10).forEach(customer => {
      customers.push({
        id: `customer-${customer.customerId}`,
        type: 'customer',
        label: customer.accountName,
        description: `Customer ID: ${customer.customerId}`,
        customerId: customer.customerId,
      });
    });

    searchQuery.data?.systems.slice(0, 10).forEach(system => {
      systems.push({
        id: `system-${system.id}`,
        type: 'system',
        label: system.machineName || system.id,
        description: `System ID: ${system.id}`,
        systemId: system.id,
      });
    });

    searchQuery.data?.licenses.slice(0, 10).forEach(license => {
      licenses.push({
        id: `license-${license.licenseId}`,
        type: 'license',
        label: license.licenseId,
        description: license.product ? `Product: ${license.product}` : 'License',
        customerId: license.customerId,
        systemId: license.systemId,
        licenseId: license.licenseId,
      });
    });

    searchQuery.data?.hardwareUnits.slice(0, 10).forEach(unit => {
      hardware.push({
        id: `hardware-${unit.serialNumber}`,
        type: 'hardware',
        label: unit.serialNumber,
        description: unit.item || 'Hardware Unit',
        customerId: unit.customerId,
        serialNumber: unit.serialNumber,
      });
    });

    return {customers, systems, licenses, hardware};
  }, [searchQuery.data]);

  const allSuggestions = useMemo(
    () => [...groupedSuggestions.customers, ...groupedSuggestions.systems, ...groupedSuggestions.licenses, ...groupedSuggestions.hardware],
    [groupedSuggestions]
  );

  const navigateToSuggestion = (suggestion: SearchSuggestion) => {
    combobox.closeDropdown();
    setQuery('');

    const routes = {
      customer: {to: '/customers/$customerId', params: {customerId: suggestion.customerId}},
      system: {to: '/systems/$systemId', params: {systemId: suggestion.systemId}},
      license: suggestion.systemId
        ? {to: '/systems/$systemId', params: {systemId: suggestion.systemId}}
        : suggestion.customerId
          ? {to: '/customers/$customerId', params: {customerId: suggestion.customerId}}
          : null,
      hardware: suggestion.customerId
        ? {to: '/customers/$customerId', params: {customerId: suggestion.customerId}}
        : null,
    };

    const route = routes[suggestion.type];
    if (route && (suggestion.customerId || suggestion.systemId)) {
      navigate(route);
    }
  };

  const optionSubmittedRef = useRef(false);

  const handleOptionSubmit = (value: string) => {
    optionSubmittedRef.current = true;
    const suggestion = allSuggestions.find(s => s.id === value);
    if (suggestion) {
      navigateToSuggestion(suggestion);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setQuery(value);
    combobox.resetSelectedOption();

    if (value.trim().length >= 2) {
      combobox.openDropdown();
    } else {
      combobox.closeDropdown();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && query.trim()) {
      // Reset the flag
      optionSubmittedRef.current = false;

      // Use setTimeout to check after Combobox has processed the event
      setTimeout(() => {
        // If no option was submitted, navigate to search results
        if (!optionSubmittedRef.current) {
          combobox.closeDropdown();
          inputRef.current?.blur();
          if (onSearch) {
            onSearch(query);
          } else {
            navigate({to: '/search', search: {q: query}});
          }
        }
      }, 0);
    }
  };

  const handleClear = () => {
    setQuery('');
    combobox.closeDropdown();
    inputRef.current?.focus();
  };

  const showDropdown = combobox.dropdownOpened && shouldSearch;

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleOptionSubmit}
      withinPortal={!centered}
      offset={0}
      classNames={{
        dropdown: classes.dropdown,
        option: classes.option,
        groupLabel: classes.groupLabel,
      }}
    >
      <Combobox.Target>
        <Box
          className={centered && showDropdown ? classes.containerWithDropdown : undefined}
          pos="relative"
          w={centered ? {base: '90%', md: '80%', lg: '70%', xl: '60%'} : undefined}
          miw={centered ? undefined : 300}
          maw={centered ? undefined : 415}
          flex={centered ? undefined : 1}
        >
          <TextInput
            ref={inputRef}
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={'Search for systems, customers, licenses or hardware units'}
            radius={centered ? 'xl' : 'md'}
            size={centered ? 'lg' : (size ?? 'md')}
            autoFocus={centered}
            aria-label="global-search-input"
            classNames={{
              input: [
                classes.input,
                centered ? classes.centeredInput : undefined,
                showDropdown ? classes.inputWithDropdown : undefined,
              ].filter(Boolean).join(' '),
            }}
            styles={{
              input: {
                '&::placeholder': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              },
            }}
            leftSection={isLoading ? <Loader size={16}/> : <IconSearch size={16}/>}
            rightSection={
              query ? (
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  onClick={handleClear}
                  aria-label="clear-search"
                >
                  <IconX size={14}/>
                </ActionIcon>
              ) : null
            }
          />
        </Box>
      </Combobox.Target>

      <Combobox.Dropdown hidden={!shouldSearch}>
        <Combobox.Options mah='min(400px, calc(100vh - 200px))' style={{overflowY: 'auto'}}>
          {groupedSuggestions.customers.length > 0 && (
            <Combobox.Group label="CUSTOMERS">
              {groupedSuggestions.customers.map((suggestion) => (
                <Combobox.Option key={suggestion.id} value={suggestion.id}>
                  <Tooltip label={suggestion.label} openDelay={500} withinPortal multiline maw={300}>
                    <Text size="sm" fw={500} truncate="end">
                      {suggestion.label}
                    </Text>
                  </Tooltip>
                  <Tooltip label={suggestion.description} openDelay={500} withinPortal multiline maw={300}>
                    <Text size="xs" c="dimmed" truncate="end">
                      {suggestion.description}
                    </Text>
                  </Tooltip>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          )}

          {groupedSuggestions.systems.length > 0 && (
            <Combobox.Group label="SYSTEMS">
              {groupedSuggestions.systems.map((suggestion) => (
                <Combobox.Option key={suggestion.id} value={suggestion.id}>
                  <Tooltip label={suggestion.label} openDelay={500} withinPortal multiline maw={300}>
                    <Text size="sm" fw={500} truncate="end">
                      {suggestion.label}
                    </Text>
                  </Tooltip>
                  <Tooltip label={suggestion.description} openDelay={500} withinPortal multiline maw={300}>
                    <Text size="xs" c="dimmed" truncate="end">
                      {suggestion.description}
                    </Text>
                  </Tooltip>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          )}

          {groupedSuggestions.licenses.length > 0 && (
            <Combobox.Group label="LICENSES">
              {groupedSuggestions.licenses.map((suggestion) => (
                <Combobox.Option key={suggestion.id} value={suggestion.id}>
                  <Tooltip label={suggestion.label} openDelay={500} withinPortal multiline maw={300}>
                    <Text size="sm" fw={500} truncate="end">
                      {suggestion.label}
                    </Text>
                  </Tooltip>
                  <Tooltip label={suggestion.description} openDelay={500} withinPortal multiline maw={300}>
                    <Text size="xs" c="dimmed" truncate="end">
                      {suggestion.description}
                    </Text>
                  </Tooltip>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          )}

          {groupedSuggestions.hardware.length > 0 && (
            <Combobox.Group label="HARDWARE">
              {groupedSuggestions.hardware.map((suggestion) => (
                <Combobox.Option key={suggestion.id} value={suggestion.id}>
                  <Tooltip label={suggestion.label} openDelay={500} withinPortal multiline maw={300}>
                    <Text size="sm" fw={500} truncate="end">
                      {suggestion.label}
                    </Text>
                  </Tooltip>
                  <Tooltip label={suggestion.description} openDelay={500} withinPortal multiline maw={300}>
                    <Text size="xs" c="dimmed" truncate="end">
                      {suggestion.description}
                    </Text>
                  </Tooltip>
                </Combobox.Option>
              ))}
            </Combobox.Group>
          )}

          {allSuggestions.length === 0 && (
            <Box p="sm">
              <Text size="sm" c="dimmed" ta="center">
                No results found for "{debouncedQuery}"
              </Text>
            </Box>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}